// import { Drawer } from 'antd';
import React, { FC, memo, useCallback, useEffect, useState } from 'react';
import { WarehouseForBooking } from 'entities/Warehouse';
import { YandexMap, YandexMarker } from 'features/YandexMap';
import { useAppDispatch } from 'app/config/storeConfig/hooks';
import { GeolocationCoords } from 'app/types/common';
import { useAppTranslation } from 'app/config/i18Config/hooks';
import { Button } from 'shared/ui/Button';
import { warehouseMapActions } from 'widgets/SelectWarehouseMap/model/slice/warehouseMapSlice';
import { ReactComponent as CloseIcon } from 'shared/assets/icons/CloseIcon.svg';
// import s from './SelectWarehouseMapMobileYandex.module.scss';
import { ModernWarehouseDrawerMobile } from './ModernWarehouseDrawerMobile';
import { ModernWarehouseCardMobile } from 'features/WarehouseCard';
import { ModernWarehousesListMobile } from 'features/WarehousesList';

interface SelectWarehouseMapMobileYandexProps {
  onCompleteStep: (warehouse: WarehouseForBooking) => void;
  markers: YandexMarker[];
  center: Nullable<GeolocationCoords>;
  selectedMarkerId: Nullable<string>;
  selectMarker: (lat: number, lng: number, markerId: string) => void;
  routeResult: Nullable<unknown>;
  onSelectMarker: (warehouseCoords: GeolocationCoords) => void;
  onGetRoute: (destination: GeolocationCoords, warehouseId: string) => Promise<void>;
  onClearRoute: () => void;
  selectedWarehouseData: WarehouseForBooking | undefined;
  warehouses: WarehouseForBooking[] | undefined;
  initialWarehousesCount: number;
  selectedWarehouseId: Nullable<string>;
  outlinedWarehouseId: Nullable<string>;
}

type MobileViewMode = 'map' | 'list';

export const SelectWarehouseMapMobileYandex: FC<SelectWarehouseMapMobileYandexProps> = memo((props) => {
  const {
    onCompleteStep,
    warehouses,
    // selectedWarehouseId,
    outlinedWarehouseId,
    selectMarker,
    onGetRoute,
    onClearRoute,
    selectedWarehouseData,
    onSelectMarker,
    // initialWarehousesCount,
    markers,
    center,
    routeResult,
    selectedMarkerId,
  } = props;

  const { t } = useAppTranslation(['booking', 'common']);
  const dispatch = useAppDispatch();

  const [mobileViewMode, setMobileViewMode] = useState<MobileViewMode>('list');
  const [isCardDrawerOpen, setIsCardDrawerOpen] = useState(false);
  const [isRouteDrawn, setIsRouteDrawn] = useState(false);

  const openCardDrawer = useCallback((): void => {
    setIsCardDrawerOpen(true);
  }, []);

  const closeCardDrawer = useCallback((): void => {
    setIsCardDrawerOpen(false);
  }, []);

  const switchToMapView = useCallback((): void => {
    setMobileViewMode('map');
  }, []);

  const switchToListView = useCallback((): void => {
    setMobileViewMode('list');
  }, []);

  const onSelectWarehouseCard = useCallback(
    (warehouse: WarehouseForBooking): void => {
      dispatch(warehouseMapActions.setSelectedCard(warehouse.warehouseId));
      openCardDrawer();
    },
    [dispatch, openCardDrawer],
  );

  const onGetRouteFromCard = useCallback(
    async (destination: GeolocationCoords, warehouseId: string): Promise<void> => {
      await onGetRoute(destination, warehouseId);
      setIsRouteDrawn(true);
    },
    [onGetRoute],
  );

  const onClearRouteFromCard = useCallback((): void => {
    onClearRoute();
    setIsRouteDrawn(false);
  }, [onClearRoute]);

  const handleViewBoxes = useCallback((): void => {
    if (selectedWarehouseData) {
      onCompleteStep(selectedWarehouseData);
    }
  }, [selectedWarehouseData, onCompleteStep]);

  // const handleSelectCard = useCallback(
  //   (coords: GeolocationCoords): void => {
  //     onSelectMarker(coords);
  //   },
  //   [onSelectMarker],
  // );

  const handleSelectCardForList = useCallback(
    (lat: number, lng: number, cardId: string): void => {
      selectMarker(lat, lng, cardId);
    },
    [selectMarker],
  );

  useEffect(() => {
    if (isRouteDrawn) {
      closeCardDrawer();
    }
  }, [closeCardDrawer, isRouteDrawn, routeResult]);

  const renderMobileView = (): JSX.Element => {
    switch (mobileViewMode) {
      case 'map':
        return (
          <>
            <YandexMap
              mapContainerClassName="w-full h-[calc(100dvh-130px)]"
              markers={markers}
              center={center}
              mapId="warehouseMapYandexMobile"
              selectedMarkerId={selectedMarkerId}
              selectMarker={selectMarker}
              routeResult={routeResult}
              onSelectMarker={onSelectMarker}
              withMarkerPopups={false}
              isMarkerClickable={false}
            />
            {routeResult && (
              <Button
                containerClassName="absolute bottom-20 right-4"
                theme="secondary"
                icon={<CloseIcon className="stroke-primaryLight" />}
                iconPosition="prev"
                onClick={onClearRoute}
              >
                {t('Clear route')}
              </Button>
            )}
            {selectedWarehouseData && (
              <div className="absolute bottom-[70px] left-4 right-4">
                <ModernWarehouseCardMobile
                  warehouse={selectedWarehouseData}
                  isOutlined={false}
                  onSelectCard={handleViewBoxes}
                  onGetRoute={onGetRouteFromCard}
                  onClearRoute={onClearRouteFromCard}
                />
              </div>
            )}
            <div className="absolute bottom-4 left-4 right-4 pt-4">
              <Button className="w-full" onClick={switchToListView}>
                {t('List mode')}
              </Button>
            </div>
          </>
        );
      case 'list':
      default:
        return (
          <div className="flex flex-col h-full">
            <div className="flex-1 overflow-y-auto pb-2">
              <ModernWarehousesListMobile
                warehouses={warehouses}
                outlinedWarehouseId={outlinedWarehouseId}
                onSelectCard={handleSelectCardForList}
                onGetRoute={onGetRouteFromCard}
                onClearRoute={onClearRouteFromCard}
                onSelectWarehouse={onSelectWarehouseCard}
              />
            </div>
            <div className="p-4">
              <Button className="w-full" onClick={switchToMapView}>
                {t('Map mode')}
              </Button>
            </div>
          </div>
        );
    }
  };

  return (
    <>
      <div className="relative h-[calc(100dvh-130px)] max-h-[calc(100dvh-130px)]">{renderMobileView()}</div>
      {selectedWarehouseData && (
        <ModernWarehouseDrawerMobile
          isOpen={isCardDrawerOpen}
          onClose={closeCardDrawer}
          warehouse={selectedWarehouseData}
          onSelectBox={() => undefined}
          onBookBox={() => {
            onCompleteStep(selectedWarehouseData);
          }}
        />
      )}
    </>
  );
});
